
import React, { useEffect, useState, Suspense } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Logger, removeFromSession } from 'wtb-utils';
import { Button, Icon, Typography } from 'wtb-components';
import { generatePayment, validateBilling } from 'api';
import {
  Divider,
  PaymentSelector,
  Terms,
  Warning
} from 'components';
import {
  useAppContext,
  useBillingContext,
  useLocaleContext,
  useTransactionContext
} from 'context';
import { ACTION_STATES, APP_STEPS } from 'constants.js';
import { cleanCustomerDataToBE } from 'utils';
import uiIcons from 'uiIcons';
import styles from './Payment.module.css';

const SelectPaymentButton = ({ tabIndex }) => {

  const history = useHistory();

  const {
    areTermsAccepted,
    transaction: { id },
    setActions,
    setPayment,
    setSign,
    setTransaction,
    setTriedToPay,
    selectedPaymentPlan
  } = useTransactionContext();
  const { setAppStep } = useAppContext();
  const { translate } = useLocaleContext();
  const { hasBillingInfo, billing, setBillingInfoError } = useBillingContext();

  const [ isLoading, setIsLoading ] = useState(false);

  // TODO: Remove when we remove avtalegiro mock
  const payment = selectedPaymentPlan?.type.replace('_MOCK', '').replace('_mock', '').toLowerCase();

  const setTermsWarning = () => setTriedToPay(true);

  const checkBilling = () => new Promise((resolve) => {
    if (!hasBillingInfo()) {
      resolve();
      return;
    }

    const isBilling = true;

    const BEData = cleanCustomerDataToBE(billing, isBilling);

    validateBilling(id, {
      planId: selectedPaymentPlan.id,
      billing: BEData
    }).then(() => {
      removeFromSession('billingInfoError');
      resolve();
    }).catch((error) => {
      setBillingInfoError(error);
      resolve();
    });
  });

  const handleClick = async () => {
    if (!areTermsAccepted) {
      setTermsWarning();
      return;
    }

    setIsLoading(true);

    checkBilling()
      .then(() => {
        generatePayment(id, { planId: selectedPaymentPlan.id })
          .then((response) => {
            if (response.actions) setActions(response.actions);
            if (response.pay) setPayment(response.pay);
            if (response.sign) setSign(response.sign);

            setTransaction((previous) => ({
              ...previous,
              state: response.state
            }));

            history.push(`/${id}/payment/${payment}`);
          })
          .catch((error) => {
            setIsLoading(false);
            Sentry.captureException(error);
            setAppStep(APP_STEPS.ERROR);
          });
      })
      .catch((error) => {
        setIsLoading(false);
        Logger.error(error);
      });
  };

  return (
    <div className={styles.selectPaymentButtonContainer}>
      <Button
        id={`pay-with-${payment || 'unknown'}`}
        className={styles.selectPaymentButton}
        onClick={handleClick}
        onKeyUp={(event) => { if (event.keyCode === 13) handleClick(); }}
        disabled={!selectedPaymentPlan?.type}
        isLoading={isLoading}
        icon={<Icon name={uiIcons.lock} className={styles.buttonIcon} />}
        iconPosition="left"
        tabIndex={tabIndex}
      >
        {selectedPaymentPlan?.type
          ? translate('payment.continueToPayment', {
            type: translate(`paymentPlans.${payment}.title`)
          })
          : translate('payment.pay')}
      </Button>
    </div>
  );
};

const Payment = () => {

  const { step } = useParams();

  const { setAppStep } = useAppContext();
  const {
    actions,
    areTermsAccepted,
    hasTerms,
    triedToPay,
    customerData,
    hasPersonalInfo
  } = useTransactionContext();
  const { translate } = useLocaleContext();

  useEffect(() => {
    if (step === APP_STEPS.PAYMENT
      && (
        actions.details === ACTION_STATES.REQUIRED
        || (
          !!customerData
          && Object.keys(customerData).length
          && !hasPersonalInfo()
        )
      )
    ) {
      Logger.log('[PAYMENT] Not enough customer data, redirecting to personal info');
      setAppStep(APP_STEPS.PERSONAL_INFORMATION);
    }
  }, [ actions, step, setAppStep, customerData, hasPersonalInfo ]);

  return (
    <div className={styles.root}>
      <Typography className={styles.text} variant="caption">{translate('payment.choosePayment')}</Typography>

      <PaymentSelector tabIndex={1} />

      {hasTerms() && (
        <Suspense fallback={<div />}>
          <Divider />

          <Terms tabIndex={8} />
        </Suspense>
      )}
      {triedToPay && !areTermsAccepted && (
        <div className={styles.warningContainer}>
          <Warning className={styles.warning} message={translate('terms.reminder')} />
        </div>
      )}

      <SelectPaymentButton tabIndex={9} />
    </div>
  );
};

export default Payment;
